import { toBaseSnapshot } from './state.js';

const ROOM_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export function createRoomCode() {
  const bytes = crypto.getRandomValues(new Uint8Array(6));
  return Array.from(bytes, byte => ROOM_ALPHABET[byte % ROOM_ALPHABET.length]).join('');
}

export function collapsePresences(state) {
  const players = new Map();
  Object.values(state || {}).flat().forEach(player => {
    if (player?.userId) players.set(player.userId, player);
  });
  return [...players.values()];
}

export class MultiplayerService {
  constructor(auth, state, onChange = () => {}) {
    this.auth = auth;
    this.state = state;
    this.onChange = onChange;
    this.lobbyChannel = null;
    this.lobbyPromise = null;
    this.roomChannel = null;
    this.lobbyUsers = [];
    this.roomUsers = [];
    this.room = null;
    this.messages = [];
    this.invites = [];
    this.connected = false;
    this.status = 'offline';
    this.lastError = '';
  }

  get user() { return this.auth.user; }
  get opponent() { return this.roomUsers.find(player => player.userId !== this.user?.id) || null; }

  profile(includeBase = false) {
    return {
      userId: this.user.id,
      name: String(this.state.playerName || 'Stamp').slice(0, 40),
      level: Math.max(1, Number(this.state.level) || 1),
      friendCode: this.state.online.friendCode || '',
      ...(includeBase ? { base: toBaseSnapshot(this.state), ready: true } : {}),
      onlineAt: new Date().toISOString()
    };
  }

  async subscribe(channel) {
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('เชื่อมต่อห้องออนไลน์หมดเวลา')), 12000);
      channel.subscribe(async status => {
        if (status === 'SUBSCRIBED') {
          window.clearTimeout(timer);
          try {
            await channel.track(this.profile(channel === this.roomChannel));
            this.status = channel === this.roomChannel ? 'room' : 'online';
            this.lastError = '';
            this.onChange();
            resolve(channel);
          }
          catch (error) { reject(error); }
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          window.clearTimeout(timer);
          this.status = 'error';
          this.lastError = 'การเชื่อมต่อ Realtime ขัดข้อง';
          this.onChange();
          reject(new Error('เชื่อมต่อ Multiplayer ไม่สำเร็จ'));
        }
        if (status === 'CLOSED') {
          if (channel === this.lobbyChannel) {
            this.status = 'offline';
            this.connected = false;
          } else if (this.lobbyChannel) {
            this.status = 'online';
          }
          this.onChange();
        }
      });
    });
  }

  async connectLobby() {
    if (!this.user) throw new Error('LOGIN_REQUIRED');
    if (this.connected && this.lobbyChannel) return this.lobbyUsers;
    if (this.lobbyPromise) return this.lobbyPromise;
    this.status = 'connecting';
    this.lobbyPromise = this.openLobby();
    this.onChange();
    try { return await this.lobbyPromise; }
    finally { this.lobbyPromise = null; }
  }

  async openLobby() {
    const token = this.auth.session?.access_token;
    await this.auth.client.realtime.setAuth(token);
    const channel = this.auth.client.channel('clash:lobby', {
      config: { private: true, presence: { key: this.user.id } }
    });
    this.lobbyChannel = channel;
    channel.on('presence', { event: 'sync' }, () => {
      this.lobbyUsers = collapsePresences(channel.presenceState());
      this.connected = true;
      this.onChange();
    });
    channel.on('broadcast', { event: 'invite' }, ({ payload }) => {
      if (payload?.targetUserId !== this.user.id || !payload?.roomCode || !payload?.fromUserId) return;
      const invite = {
        fromUserId: String(payload.fromUserId),
        fromName: String(payload.fromName || 'ผู้พิทักษ์').slice(0, 40),
        roomCode: String(payload.roomCode).slice(0, 6),
        sentAt: Number(payload.sentAt) || Date.now()
      };
      this.invites = [invite, ...this.invites.filter(item => item.roomCode !== invite.roomCode)].slice(0, 3);
      this.onChange();
    });
    try { await this.subscribe(channel); }
    catch (error) { this.lobbyChannel = null; this.status = 'error'; this.lastError = error.message; await this.auth.client.removeChannel(channel); throw error; }
    this.connected = true;
    this.onChange();
    return this.lobbyUsers;
  }

  async createRoom() {
    if (!this.user) throw new Error('LOGIN_REQUIRED');
    await this.connectLobby();
    await this.leaveRoom();
    await this.auth.client.from('clash_multiplayer_rooms')
      .update({ status: 'closed' })
      .eq('host_user_id', this.user.id)
      .eq('status', 'waiting');

    let created;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const { data, error } = await this.auth.client.from('clash_multiplayer_rooms').insert({
        room_code: createRoomCode(),
        host_user_id: this.user.id,
        host_name: this.profile().name,
        host_level: this.profile().level,
        host_base: toBaseSnapshot(this.state)
      }).select('*').single();
      if (!error) { created = data; break; }
      if (error.code !== '23505') throw error;
    }
    if (!created) throw new Error('สร้าง Room Code ไม่สำเร็จ');
    await this.joinMembership(created.id);
    await this.enterRoom(created);
    return created;
  }

  async joinMembership(roomId) {
    const profile = this.profile();
    const { error } = await this.auth.client.from('clash_multiplayer_members').upsert({
      room_id: roomId,
      user_id: this.user.id,
      display_name: profile.name,
      player_level: profile.level,
      joined_at: new Date().toISOString()
    }, { onConflict: 'room_id,user_id' });
    if (error) throw error;
  }

  async joinRoom(code) {
    if (!this.user) throw new Error('LOGIN_REQUIRED');
    const normalized = String(code || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    if (normalized.length !== 6) throw new Error('Room Code ต้องมี 6 ตัว');
    await this.connectLobby();
    await this.leaveRoom();
    const { data, error } = await this.auth.client.from('clash_multiplayer_rooms')
      .select('*').eq('room_code', normalized).eq('status', 'waiting').gt('expires_at', new Date().toISOString()).maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('ไม่พบห้องนี้ หรือห้องหมดอายุแล้ว');
    const { data: members, error: memberError } = await this.auth.client.from('clash_multiplayer_members')
      .select('user_id').eq('room_id', data.id);
    if (memberError) throw memberError;
    if ((members || []).length >= 2 && !members.some(member => member.user_id === this.user.id)) throw new Error('ROOM_FULL');
    await this.joinMembership(data.id);
    await this.enterRoom(data);
    return data;
  }

  async quickMatch() {
    if (!this.user) throw new Error('LOGIN_REQUIRED');
    await this.connectLobby();
    const { data, error } = await this.auth.client.from('clash_multiplayer_rooms')
      .select('*').eq('status', 'waiting').gt('expires_at', new Date().toISOString())
      .neq('host_user_id', this.user.id).order('created_at', { ascending: true }).limit(20);
    if (error) throw error;
    const liveIds = new Set(this.lobbyUsers.map(player => player.userId));
    const myLevel = Number(this.profile().level) || 1;
    const liveRooms = (data || [])
      .filter(candidate => liveIds.has(candidate.host_user_id))
      .sort((a, b) => Math.abs((Number(a.host_level) || 1) - myLevel) - Math.abs((Number(b.host_level) || 1) - myLevel));
    for (const candidate of liveRooms) {
      try { return await this.joinRoom(candidate.room_code); }
      catch (error) { if (error.message !== 'ROOM_FULL') throw error; }
    }
    return this.createRoom();
  }

  async invitePlayer(player) {
    if (!player?.userId || player.userId === this.user?.id) return;
    if (!this.room) await this.createRoom();
    await this.lobbyChannel.send({ type: 'broadcast', event: 'invite', payload: {
      targetUserId: player.userId,
      fromUserId: this.user.id,
      fromName: this.profile().name,
      roomCode: this.room.room_code,
      sentAt: Date.now()
    } });
  }

  dismissInvite(roomCode) {
    this.invites = this.invites.filter(invite => invite.roomCode !== roomCode);
    this.onChange();
  }

  async enterRoom(room) {
    if (this.roomChannel) await this.leaveRoom();
    this.room = room;
    this.messages = [];
    const channel = this.auth.client.channel(`clash:room:${room.room_code}`, {
      config: { private: true, presence: { key: this.user.id }, broadcast: { self: true } }
    });
    this.roomChannel = channel;
    channel.on('presence', { event: 'sync' }, () => {
      const previousIds = new Set(this.roomUsers.map(player => player.userId));
      const nextUsers = collapsePresences(channel.presenceState());
      const nextIds = new Set(nextUsers.map(player => player.userId));
      if (this.roomUsers.length) {
        nextUsers.filter(player => !previousIds.has(player.userId) && player.userId !== this.user.id).forEach(player => {
          this.messages = [...this.messages.slice(-11), { kind: 'system', text: `${player.name} เข้าห้องแล้ว`, sentAt: Date.now() }];
        });
        this.roomUsers.filter(player => !nextIds.has(player.userId) && player.userId !== this.user.id).forEach(player => {
          this.messages = [...this.messages.slice(-11), { kind: 'system', text: `${player.name} ออกจากห้อง`, sentAt: Date.now() }];
        });
      }
      this.roomUsers = nextUsers;
      this.onChange();
    });
    channel.on('broadcast', { event: 'chat' }, ({ payload }) => {
      this.messages = [...this.messages.slice(-11), payload];
      this.onChange();
    });
    channel.on('broadcast', { event: 'result' }, ({ payload }) => {
      this.messages = [...this.messages.slice(-11), { ...payload, kind: 'result' }];
      this.onChange();
    });
    try { await this.subscribe(channel); }
    catch (error) { this.roomChannel = null; this.room = null; this.status = 'error'; this.lastError = error.message; await this.auth.client.removeChannel(channel); throw error; }
    this.onChange();
  }

  async sendChat(text) {
    if (!this.roomChannel) return;
    const message = String(text || '').trim().slice(0, 80);
    if (!message) return;
    await this.roomChannel.send({ type: 'broadcast', event: 'chat', payload: {
      userId: this.user.id, name: this.profile().name, text: message, sentAt: Date.now()
    } });
  }

  async sendResult(result) {
    if (!this.roomChannel) return;
    await this.roomChannel.send({ type: 'broadcast', event: 'result', payload: {
      userId: this.user.id, name: this.profile().name,
      percent: result.percent, marks: result.marks, sentAt: Date.now()
    } });
  }

  async recordMatch(opponent, result) {
    if (!this.user || !opponent?.userId) return;
    const { error } = await this.auth.client.from('clash_multiplayer_matches').insert({
      user_id: this.user.id,
      opponent_user_id: opponent.userId,
      opponent_name: String(opponent.name || 'Rival').slice(0, 40),
      room_code: this.room?.room_code || opponent.roomCode || null,
      destruction_percent: Math.max(0, Math.min(100, Math.round(result.percent))),
      spirit_marks: Math.max(0, Math.min(3, Math.round(result.marks))),
      won: result.marks > 0
    });
    if (error) throw error;
  }

  async history(limit = 8) {
    if (!this.user) return [];
    const { data, error } = await this.auth.client.from('clash_multiplayer_matches')
      .select('opponent_name, destruction_percent, spirit_marks, won, created_at')
      .order('created_at', { ascending: false }).limit(limit);
    if (error) throw error;
    return data || [];
  }

  async leaveRoom() {
    const room = this.room;
    if (this.roomChannel) {
      try { await this.roomChannel.untrack(); } catch {}
      await this.auth.client.removeChannel(this.roomChannel);
    }
    this.roomChannel = null;
    this.roomUsers = [];
    this.room = null;
    this.messages = [];
    if (room && this.user) {
      await this.auth.client.from('clash_multiplayer_members').delete()
        .eq('room_id', room.id).eq('user_id', this.user.id);
      if (room.host_user_id === this.user.id) {
        await this.auth.client.from('clash_multiplayer_rooms').update({ status: 'closed' }).eq('id', room.id);
      }
    }
    this.status = this.lobbyChannel ? 'online' : 'offline';
    this.onChange();
  }

  async disconnect() {
    await this.leaveRoom();
    if (this.lobbyChannel) {
      try { await this.lobbyChannel.untrack(); } catch {}
      await this.auth.client.removeChannel(this.lobbyChannel);
    }
    this.lobbyChannel = null;
    this.lobbyPromise = null;
    this.lobbyUsers = [];
    this.invites = [];
    this.connected = false;
    this.status = 'offline';
    this.onChange();
  }
}
